import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import ForkInTheRoad from "@/components/ForkInTheRoad";
import { getAllProjects } from "@/lib/markdown";
import { PROJECT_ERAS } from "@/data/projectEras";
import type { Era, Project } from "@/types";

/** One road's project list, revealed under the fork once that road is chosen. */
const RoadProjects: React.FC<{ era: Era; projects: Project[] }> = ({
  era,
  projects,
}) => {
  const { label, when, motto, dark } = PROJECT_ERAS[era];
  return (
    <section
      aria-labelledby={`era-${era}`}
      className={clsx(
        "fork-reveal font-mono pb-2 border-t-2 border-[#111827]",
        dark ? "bg-[#111827] text-white" : "bg-white text-[#111827]",
      )}
    >
      <header className="px-5 sm:px-8 pt-6 pb-5">
        <div className="flex items-baseline justify-between gap-4">
          <h2
            id={`era-${era}`}
            className="text-lg font-bold uppercase tracking-wide"
          >
            {label}
          </h2>
          <span className="text-xs opacity-60 whitespace-nowrap">{when}</span>
        </div>
        <p className="mt-2 text-sm opacity-70">{motto}</p>
      </header>

      {projects.length === 0 ? (
        <p className="px-5 sm:px-8 py-4 text-sm opacity-70">
          Nothing down this road yet.
        </p>
      ) : (
        <ol>
          {projects.map((project, index) => (
            <li key={project.slug}>
              <Link
                to={`/projects/${project.slug}`}
                className={clsx(
                  "grid grid-cols-[2rem_1fr_auto] gap-x-3 px-5 sm:px-8 py-4 border-t transition-colors duration-200 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-current",
                  dark
                    ? "border-white/20 hover:bg-white hover:text-[#111827]"
                    : "border-[#111827]/15 hover:bg-[#111827] hover:text-white",
                )}
              >
                <span className="text-xs opacity-50 tabular-nums pt-1">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span>
                  <span className="block font-bold">
                    {project.title}
                    {project.status === "archived" && (
                      <span className="ml-2 align-middle text-[0.65rem] font-normal uppercase tracking-wide border border-current px-1.5 py-0.5 opacity-60">
                        Archived
                      </span>
                    )}
                  </span>
                  <span className="block text-sm opacity-80 mt-1">
                    {project.summary}
                  </span>
                  <span className="block text-xs opacity-60 mt-2">
                    {project.tech}
                  </span>
                </span>
                <span aria-hidden="true" className="pt-0.5">
                  →
                </span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

const Projects: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const roadParam = searchParams.get("road");
  const road: Era | null =
    roadParam === "pre" || roadParam === "post" ? roadParam : null;
  const [hovered, setHovered] = useState<Era | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    getAllProjects().then(setProjects);
  }, []);

  // The page opens on the fork alone; a road's projects appear once it's chosen.
  // Choosing the same road again walks back to the fork.
  const chooseRoad = (next: Era) =>
    setSearchParams(road === next ? {} : { road: next }, { replace: true });

  return (
    <div>
      <h1 className="sr-only">Projects</h1>

      <div className="border-2 border-[#111827]">
        <ForkInTheRoad
          road={road}
          hovered={hovered}
          onHover={setHovered}
          onChoose={chooseRoad}
        />

        <div
          role="group"
          aria-label="Choose a road"
          className="grid grid-cols-2 border-t-2 border-[#111827] font-mono text-sm"
        >
          {(["pre", "post"] as const).map((era) => {
            const count = projects.filter((p) => p.era === era).length;
            return (
              <button
                key={era}
                type="button"
                aria-pressed={road === era}
                onClick={() => chooseRoad(era)}
                onMouseEnter={() => setHovered(era)}
                onMouseLeave={() => setHovered(null)}
                className={clsx(
                  "touch-target px-3 sm:px-5 py-3 transition-colors duration-200 focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-current",
                  era === "pre" &&
                    "text-left bg-white text-[#111827] hover:bg-[#111827] hover:text-white",
                  era === "post" &&
                    "text-right bg-[#111827] text-white hover:bg-white hover:text-[#111827]",
                  road === era && "font-bold underline underline-offset-4",
                )}
              >
                {era === "pre" && "← "}
                {PROJECT_ERAS[era].label}
                <span className="opacity-60"> ({count})</span>
                {era === "post" && " →"}
              </button>
            );
          })}
        </div>

        {road && (
          <RoadProjects
            key={road}
            era={road}
            projects={projects.filter((p) => p.era === road)}
          />
        )}
      </div>
    </div>
  );
};

export default Projects;
